import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { previewOffering, type OfferingPreview } from '../../lib/personas';
import { simulate } from '../../lib/offeringMath';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatMoney, formatMoneyCompact } from '../../lib/format';
import { OfferingLegends, ForwardLookingNote } from '../../components/OfferingDisclosures';
import { SimulationWatermark } from '../../components/SimulationWatermark';
import './AdminOfferingPreview.css';

/**
 * "Preview as public would see it" — the deal page for an offering that is NOT public.
 *
 * /invest is empty until a raise is opened, and opening one is a real public securities
 * solicitation. This renders the identical payload the public route builds, from a
 * separate admin-only endpoint, so a draft can be reviewed without publishing it.
 *
 * WATERMARKED THROUGHOUT, deliberately heavily. A screenshot of this page must not be
 * mistakeable for the live site: a fixed header bar, a repeating diagonal watermark across
 * the whole surface, and a panel stating exactly why the public cannot see it. The
 * failure mode worth designing against is not an admin being confused — it is this
 * screenshot ending up somewhere as evidence of what was on offer.
 *
 * Reading this publishes nothing. The offering stays absent from the public API.
 */
/**
 * Where the sell-through slider starts.
 *
 * NOT a live figure, and it cannot be one: neither the public offering endpoint nor this
 * admin preview selects quantity_sold. publicOfferings.js withholds it deliberately -
 * live sales are performance of the offering rather than a term of it - and the preview
 * shares that shape so an admin sees exactly what a visitor would. So 50% is an openly
 * arbitrary midpoint, and divergence from it is measured against that, not against sales.
 */
const BASELINE_SELL_THROUGH = 50;

export function AdminOfferingPreview() {
  const { offeringId } = useParams<{ offeringId: string }>();
  const [offering, setOffering] = useState<OfferingPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sellThrough, setSellThrough] = useState(BASELINE_SELL_THROUGH);
  const [shares, setShares] = useState<number | null>(null);

  // Set the moment a control leaves its baseline, and never cleared by returning to it.
  //
  // Sticky on purpose. Someone who models 95% sell-through, screenshots it, then drags
  // back to the default has still produced the screenshot. Clearing the mark would make
  // the page look untouched at exactly the moment the projection had been driven hardest,
  // and the whole point of the mark is what leaves the screen, not what is on it now.
  const [isSimulating, setIsSimulating] = useState(false);

  useEffect(() => {
    if (!offeringId) return;
    let cancelled = false;
    setLoading(true);
    previewOffering(offeringId)
      .then((result) => {
        if (cancelled) return;
        setOffering(result);
        setShares(result.structure.minimumShares);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.status === 409
            ? 'This is a 506(b) offering. There is no public view to preview, a private '
              + 'offering is never shown publicly.'
            : err instanceof ApiError ? err.message : 'Could not load the preview.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [offeringId]);

  const projection = useMemo(() => {
    if (!offering || shares == null) return null;
    const soldByTier = Object.fromEntries(
      offering.ticketTiers.map((t) => [t.name, Math.round((t.quantityTotal * sellThrough) / 100)]),
    );
    return simulate(offering, { soldByTier, shares });
  }, [offering, sellThrough, shares]);

  if (loading) return <p className="admin-page__state">Loading preview…</p>;

  if (error || !offering) {
    return (
      <div className="admin-page">
        <Link to="/admin/opportunities" className="preview__back">← Opportunities</Link>
        <div className="alert alert--error" role="alert">
          {error ?? 'Could not load the preview.'}
        </div>
      </div>
    );
  }

  const capacity = offering.ticketTiers.reduce((n, t) => n + t.quantityTotal, 0);

  return (
    <div className="preview" data-surface="invest">
      {/* Fixed, always on screen, regardless of scroll position. */}
      <div className="preview__bar" role="alert">
        <strong>ADMIN PREVIEW, NOT LIVE, NOT PUBLIC</strong>
        <span>No one outside this console can see this offering.</span>
        <Link to="/admin/opportunities" className="preview__bar-link">Back to opportunities</Link>
      </div>

      <div className="preview__stage">
        {/* The repeating diagonal watermark sits above the content but ignores pointer
            events, so the page stays usable while never appearing clean in a capture. */}
        <div className="preview__watermark" aria-hidden="true" />

        <div className="preview__content">
          <section className="preview__why">
            <h2>Why the public cannot see this</h2>
            {offering.liveStatus.reasonsNotPublic.length > 0 ? (
              <ul>
                {offering.liveStatus.reasonsNotPublic.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            ) : (
              <p>
                Nothing is blocking it, this offering already meets every condition for
                public listing.
              </p>
            )}
            <p className="preview__why-note">
              Opening a 506(c) raise is a real public securities solicitation. This preview
              changes nothing: the offering is still absent from the public marketplace.
            </p>
          </section>

          {/* ── From here down, the public deal page ─────────────────────── */}
          <header className="offering-hero">
            <p className="offering-hero__eyebrow mono">
              Reg D · Rule 506(c) · {offering.securityType}
            </p>
            <h1 className="offering-hero__title display">{offering.title}</h1>
            <p className="offering-hero__event">
              {offering.event.title} · {formatDateShort(offering.event.eventDate)}
            </p>
            {offering.description && (
              <p className="offering-hero__pitch">{offering.description}</p>
            )}
          </header>

          <section className="offering-terms">
            <div className="offering-term">
              <span className="offering-term__label">Target raise</span>
              <span className="offering-term__value mono">
                {formatMoneyCompact(offering.targetRaiseCents)}
              </span>
            </div>
            <div className="offering-term">
              <span className="offering-term__label">Minimum investment</span>
              <span className="offering-term__value mono">
                {formatMoneyCompact(offering.minimumInvestmentCents)}
              </span>
            </div>
            <div className="offering-term">
              <span className="offering-term__label">Price per unit</span>
              <span className="offering-term__value mono">
                {formatMoney(offering.structure.pricePerShareCents)}
              </span>
            </div>
            <div className="offering-term">
              <span className="offering-term__label">Closes</span>
              <span className="offering-term__value mono">
                {offering.closesAt ? formatDateShort(offering.closesAt) : 'TBA'}
              </span>
            </div>
          </section>

          <section className="sim sim-anchor">
            <SimulationWatermark active={isSimulating} />
            <h2 className="sim__heading">Model a position</h2>
            <div className="sim__controls">
              <div className="sim__control">
                <label className="field__label" htmlFor="pv-sell">
                  Hypothetical sell-through, {sellThrough}% of {capacity.toLocaleString()} seats
                  <span className="sim-live-anchor">
                    Live: {capacity.toLocaleString()} seats on sale
                  </span>
                </label>
                <input
                  id="pv-sell" type="range" min={0} max={100} step={5}
                  value={sellThrough}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSellThrough(next);
                    if (next !== BASELINE_SELL_THROUGH) setIsSimulating(true);
                  }}
                />
              </div>
              <div className="sim__control">
                <label className="field__label" htmlFor="pv-shares">
                  Units held, minimum {offering.structure.minimumShares}
                  <span className="sim-live-anchor">
                    Live: {offering.structure.totalShares.toLocaleString()} units at{' '}
                    {formatMoney(offering.structure.pricePerShareCents)}
                  </span>
                </label>
                <input
                  id="pv-shares" className="field__input mono" type="number"
                  min={offering.structure.minimumShares}
                  max={offering.structure.totalShares}
                  value={shares ?? offering.structure.minimumShares}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setShares(next);
                    if (next !== offering.structure.minimumShares) setIsSimulating(true);
                  }}
                />
              </div>
            </div>

            {projection && (
              <>
                <div className="sim__result">
                  <div className="sim__result-block">
                    <span className="sim__result-label">You would put in</span>
                    <span className="sim__result-value mono">
                      {formatMoney(projection.investedCents)}
                    </span>
                  </div>
                  <div className="sim__result-block sim__result-block--headline">
                    <span className="sim__result-label">
                      {offering.simulator.resultLabel} position
                    </span>
                    <span
                      className={
                        projection.gainCents < 0
                          ? 'sim__result-value mono is-negative'
                          : 'sim__result-value mono'
                      }
                    >
                      {formatMoney(projection.netPositionCents)}
                    </span>
                    <span className="sim__result-qualifier">{offering.simulator.qualifier}</span>
                  </div>
                </div>
                <p className="sim__caveat" role="note">
                  <strong>Production costs are not included.</strong> Real settlement
                  subtracts documented production costs before anything is distributed, so
                  every figure here is higher than an actual distribution would be.
                </p>
                <ForwardLookingNote text={offering.simulator.disclaimer} />
              </>
            )}
          </section>

          <OfferingLegends disclosures={offering.disclosures} />
        </div>
      </div>
    </div>
  );
}
